"use client"

/**
 * Profile menu role switch. The role applies across the app (blueprints,
 * category standards, what an author may tag) and switches instantly without
 * signing out, unlike the Sign-in flow menu.
 */

import {
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu"
import { syncExamRoleFromLoginSession } from "@/contexts/exam-role-context-shared"
import { useExamRole } from "@/contexts/exam-role-context"
import {
  applyExamBlueprintRole,
  EXAM_BLUEPRINT_ROLE_OPTIONS,
  type ExamBlueprintRoleId,
} from "@/lib/exam-blueprint-access"

function RoleOptionItem({ label, hint }: { label: string; hint: string }) {
  return (
    <span className="flex flex-col gap-0.5">
      <span>{label}</span>
      <span className="text-xs text-muted-foreground">{hint}</span>
    </span>
  )
}

export function ExamRoleMenu() {
  const { role } = useExamRole()
  const activeRole: ExamBlueprintRoleId =
    role === "Admin" ? "program-director" : "faculty"

  const activeLabel =
    EXAM_BLUEPRINT_ROLE_OPTIONS.find((option) => option.id === activeRole)
      ?.label ?? "Program director"

  function handleChange(value: string) {
    applyExamBlueprintRole(value as ExamBlueprintRoleId)
    syncExamRoleFromLoginSession()
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <i className="fa-light fa-user-tie" aria-hidden="true" />
        Role
        <span className="ms-auto pe-1 text-xs text-muted-foreground">
          {activeLabel}
        </span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-72">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Applies across the app
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup value={activeRole} onValueChange={handleChange}>
          {EXAM_BLUEPRINT_ROLE_OPTIONS.map((option) => (
            <DropdownMenuRadioItem
              key={option.id}
              value={option.id}
              className="items-start"
            >
              <RoleOptionItem label={option.label} hint={option.hint} />
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}
